import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

/**
 * MultiCarousel
 * @param {object[]} items - 렌더링할 아이템 배열 [Required]
 * @param {(item: any, index: number) => React.ReactNode} renderItem - 아이템 렌더러 [Required]
 * @param {{xs?: number, sm?: number, md?: number, lg?: number, xl?: number}} visible - 브레이크포인트별 동시 노출 개수 [Optional]
 * @param {number} gap - 아이템 간 간격(px) [Optional, 기본값: 16]
 * @param {number} step - 한 번에 이동할 아이템 수 [Optional, 기본값: 1]
 * @param {boolean} showDivider - 아이템 사이에 수직선 표시 여부 [Optional, 기본값: false]
 * @param {string} dividerColor - 수직선 색상 [Optional, 기본값: 'divider']
 */
const MultiCarousel = ({ items = [], renderItem, visible = { xs: 1, sm: 2, md: 4, lg: 4 }, gap = 16, step = 1, showDivider = false, dividerColor = 'divider' }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [index, setIndex] = useState(0);

  const visibleCount = useMemo(() => {
    if (isLgUp) return visible.lg ?? visible.xl ?? 4;
    if (isMd) return visible.md ?? 3;  
    if (isSm) return visible.sm ?? 2;
    return visible.xs ?? 1; // xs (mobile)
  }, [isSm, isMd, isLgUp, visible]);

  const maxIndex = Math.max(0, (items.length || 0) - visibleCount);

  useEffect(() => {
    // clamp when responsive changes
    setIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    let ro;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  const itemWidth = useMemo(() => {
    if (containerWidth <= 0 || visibleCount <= 0) return 0;
    
    const totalGap = gap * (visibleCount - 1);
    const availableWidth = containerWidth - totalGap;
    const calculatedWidth = Math.floor(availableWidth / visibleCount);
    
    return Math.max(50, calculatedWidth);
  }, [containerWidth, visibleCount, gap]);

  const handlePrev = () => setIndex((prev) => Math.max(0, prev - step));
  const handleNext = () => setIndex((prev) => Math.min(maxIndex, prev + step));

  const translateX = -(index * (itemWidth + gap));

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      contain: 'layout style',
      overflow: 'hidden',
    }}>
      {/* Track */}
      <Box 
        ref={containerRef} 
        sx={{ 
          overflow: 'hidden', 
          width: '100%', 
          maxWidth: '100%',
          minWidth: 0,
          contain: 'layout style',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: `${gap}px`,
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: 'transform 300ms ease',
            willChange: 'transform',
            width: `${(itemWidth + gap) * items.length}px`,
            maxWidth: `${(itemWidth + gap) * items.length}px`,
          }}
        >
          {items.map((item, idx) => {
            // 현재 보이는 범위에서 마지막 아이템인지 확인
            const isLastVisible = idx === Math.min(index + visibleCount - 1, items.length - 1);
            
            return (
              <Box 
                key={idx} 
                sx={{ 
                  flex: '0 0 auto', 
                  width: itemWidth,
                  maxWidth: itemWidth,
                  minWidth: itemWidth,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': showDivider && !isLastVisible ? {
                    content: '""',
                    position: 'absolute',
                    right: `-${gap / 2}px`,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    backgroundColor: dividerColor,
                  } : {}
                }}
              >
                {renderItem(item, idx)}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Nav Buttons - Magazine Style */}
      <IconButton
        onClick={handlePrev}
        disabled={index <= 0}
        sx={{
          position: 'absolute',
          top: '50%',
          left: { xs: 4, sm: 8, md: 12 }, // 컨테이너 안쪽으로 이동
          transform: 'translateY(-50%)',
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: 0,
          padding: { xs: 0.5, sm: 0.75, md: 1 },
          minWidth: 'auto',
          color: index <= 0 ? 'text.disabled' : 'primary.main',
          opacity: index <= 0 ? 0.3 : 1,
          transition: 'opacity 0.2s',
          zIndex: 2,
          pointerEvents: 'auto',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            opacity: index <= 0 ? 0.3 : 0.7,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            color: 'text.disabled',
          },
        }}
      >
        <IconChevronLeft size={isXs ? 16 : isSm ? 20 : isMd ? 24 : 28} stroke={2} />
      </IconButton>
      <IconButton
        onClick={handleNext}
        disabled={index >= maxIndex}
        sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: 4, sm: 8, md: 12 }, // 컨테이너 안쪽으로 이동
          transform: 'translateY(-50%)',
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: 0,
          padding: { xs: 0.5, sm: 0.75, md: 1 },
          minWidth: 'auto',
          color: index >= maxIndex ? 'text.disabled' : 'primary.main',
          opacity: index >= maxIndex ? 0.3 : 1,
          transition: 'opacity 0.2s',
          zIndex: 2,
          pointerEvents: 'auto',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            opacity: index >= maxIndex ? 0.3 : 0.7,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            color: 'text.disabled',
          },
        }}
      >
        <IconChevronRight size={isXs ? 16 : isSm ? 20 : isMd ? 24 : 28} stroke={2} />
      </IconButton>
    </Box>
  );
};

export default MultiCarousel;
